<template>
  <v-container fluid pa-0>
    <v-progress-linear v-if="!isMemberItemLoaded && !loadingError" color="orange" class="mt-0" indeterminate />
    <v-layout justify-center row wrap v-else-if="!isMemberItemLoaded && loadingError">
      <v-flex xs12 pa-0>
        <v-alert type="error" value=true>
          <v-layout row wrap>
            <v-flex xs10>
              <h2 class="headline">An Error Occurred loading this Members Profile.</h2>
              <p><em>Please Refresh the page and try again</em></p>
            </v-flex>
            <v-spacer/>
            <v-flex xs2 text-xs-right>
              <v-btn depressed outline color="white" @click="$router.go()">
                Refresh
              </v-btn>
              <v-btn depressed outline color="white" @click="$router.back()">
                Go Back
              </v-btn>
            </v-flex>
          </v-layout>
        </v-alert>
      </v-flex>
    </v-layout>
    <v-layout justify-center row wrap v-else>
      <v-flex xs12 pa-0 pt-2 pl-2>
        <a @click="$router.back()"><p small class="black--text ma-0" style="text-decoration:underline;">&#60;&#60; Back to Member List</p></a>
      </v-flex>
      <v-progress-linear v-if="pdfLoading" color="orange" class="mt-0" indeterminate />
      <v-flex xs12 pa-0 pl-1>
        <v-layout row wrap>
          <v-flex xs12 px-2>
            <h1>
              <span class="mr-4">{{`${memberItem.details.firstName} ${memberItem.details.lastName}`}}</span>
              <v-menu offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn outline small class="ma-0 mr-4 right"  v-on="on">
                    user settings
                  </v-btn>
                </template>
                <v-list class="pa-2">
                  <v-list-tile v-if="memberItem.membership && memberItem.membership.type !== 'life'" @click="lifeConfirm = true" key="lifeCheck">
                    <v-list-tile-title>
                      <h3 class="subheading">Award Life Membership</h3>
                    </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile v-if="memberItem.membership" @click="downloadCertificate()" key="certCheck">
                    <v-list-tile-title>
                      <h3 class="subheading">View Certificate</h3>
                    </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile :disabled="isMembershipExpired()" @click="deactivateConfirm = true" key="deactivateCheck">
                    <v-list-tile-title>
                      <h3 class="subheading">Deactivate Membership</h3>
                    </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile key="resetCheck" @click="resetModal = true">
                    <v-list-tile-title>
                      <h3 class="subheading">Reset User Password</h3>
                    </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile key="archiveCheck" @click="archiveConfirm = true">
                    <v-list-tile-title>
                      <h3 class="subheading">Archive Member</h3>
                    </v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </h1>
          </v-flex>
        </v-layout>
        <v-layout row wrap mb-2>
          <v-flex xs12 xl6 pa-2>
            <v-card-title class="pa-0">
              <h2 class="headline mb-0">Account Details</h2>
              <v-spacer/>
              <span v-show="metaSaved" class="green--text">Saved!</span>
              <span v-show="metaError" class="red--text">Error!</span>
              <v-btn small outline :loading="metaSaving" color="success" :disabled="!hasMetaChanged" @click="saveChanges()">Save Changes</v-btn>
              <v-btn v-if="hasMetaChanged" small outline @click="cancelChanges()" title="Undo Edited Changes to User Information">Cancel</v-btn>
            </v-card-title>
            <v-card-text class="pa-0 mb-2">
              <v-layout row wrap pa-0 mt-2>
                <v-flex xs12>
                  <h3 v-if="!userStatus" class="subheading">Account Status: <v-progress-circular indeterminate color="grey" size="24" /></h3>
                  <h3 class="subheading">Account Status: <span :class="userStatusClass()">{{userStatus}}</span><v-btn v-if="userStatus === 'UNCONFIRMED'" @click="resendActivationEmail()" :loading="resendLoading" small outlined class="ml-4">Resend Activation Email</v-btn></h3>
                  <h3 class="subheading">Account Created: {{formatDate(memberItem.UserCreateDate, 'since')}}</h3>
                  <h3 class="subheading">Account Last Modified: {{formatDate(memberItem.UserLastModifiedDate, 'full')}}</h3>
                  <h3 class="subheading">MailChimp:
                    <span v-if="memberItem.mailchimpId" class="success--text">linked</span>
                    <span v-else class="error--text">not linked</span>
                  </h3>
                </v-flex>
              </v-layout>
            </v-card-text>
            <v-card-text class="pa-0 mt-2">
              <v-flex xs12>
                <h3>Personal Information</h3>
              </v-flex>
              <v-layout row wrap>
                <v-flex xs12 sm6 md4 pr-2>
                  <v-text-field
                    label="Given Name"
                    placeholder="Submit your First Name"
                    v-model="firstName"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md8 pr-2>
                  <v-text-field
                    label="Family Name"
                    placeholder="Submit your First Name"
                    v-model="lastName"
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12 sm6 md4 pr-2>
                  <v-text-field
                    label="Primary Contact Number"
                    v-model="phoneNumber"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md8 pr-2>
                  <v-text-field
                    label="Email"
                    placeholder="Your Email Address"
                    v-model="email"
                  ></v-text-field>
                </v-flex>
                <v-divider class="orange sectionDivider" />
                <v-flex xs12 mt-2>
                  <h3>Address</h3>
                </v-flex>
                <v-flex xs12 sm6 md8 pr-2>
                  <v-text-field
                    label="Street"
                    placeholder="Add Your Street Address"
                    v-model="street"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4 pr-2>
                  <v-text-field
                    label="Postcode"
                    minLength="4"
                    mask="####"
                    placeholder="Your Post Code"
                    v-model="postcode"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md8 pr-2>
                  <v-text-field
                    label="Suburb"
                    placeholder="Add Your Suburb"
                    v-model="suburb"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4 pr-2>
                  <v-select
                    label="State"
                    :items="stateItems"
                    v-model="state"
                  ></v-select>
                </v-flex>
                <v-divider class="orange sectionDivider mb-2" />
                <v-flex xs12>
                  <h3>Secondary Contact Information</h3>
                </v-flex>
                <v-flex xs12 sm6 pr-2>
                  <v-text-field
                    label="Contact Number"
                    placeholder="Add a Secondary Contact Number Here"
                    v-model="secondNumber"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    label="Contact Name"
                    placeholder="Name of Secondary Contact"
                    v-model="secondName"
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-divider class="orange sectionDivider" />
                <v-flex xs12 mt-2>
                  <h3>Family Riders</h3>
                </v-flex>
                <v-layout row wrap v-for="item in riders" :key="item.firstName">
                  <v-flex xs12 md5 pr-2>
                    <v-text-field
                      label="First Name"
                      v-model="item.firstName"
                      @keyup="ridersToggle = true"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 md5 pr-2>
                    <v-text-field
                      label="Last Name"
                      v-model="item.lastName"
                      @keyup="ridersToggle = true"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 md2>
                    <v-text-field
                      label="Bike Number"
                      v-model="item.bikeNo"
                      @keyup="ridersToggle = true"
                    ></v-text-field>
                  </v-flex>
                </v-layout>
              </v-layout>
            </v-card-text>
          </v-flex>
          <v-flex xs12 xl6 pa-2>
            <v-card-title class="pa-0">
              <h2 class="headline mb-0">
                Membership Details
              </h2>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-layout row wrap pa-0 mt-2>
                <v-flex xs12>
                  <h3 class="subheading" v-if="memberItem.membership.membershipNo">Membership Number: {{ `#${('0000' + memberItem.membership.membershipNo).slice(-4)}` }}</h3>
                  <h3 class="subheading">Member Since: {{formatDate(memberItem.membership.memberSince, 'since')}}</h3>
                  <h3 class="subheading">Status: <span class="memberStatus" :class="memberStatusClass()">{{memberStatus()}}</span></h3>
                </v-flex>
              </v-layout>
              <v-layout row wrap mt-2 v-if="hasUserRegistered">
                <v-flex xs12>
                  <h3 class="mb-0">Current Membership</h3>
                </v-flex>
              </v-layout>
              <v-layout row wrap v-if="hasUserRegistered">
                <v-flex xs12 md6 pr-2>
                  <v-text-field
                    readonly
                    label="Membership Type"
                    :value="(memberItem.membership.type === 'life') ? 'MCTC Life Membership': 'MCTC Standard Membership'"
                  />
                </v-flex>
                <v-flex xs12 md6 pr-2>
                  <v-text-field
                    readonly
                    label="Paid"
                    placeholder="No Information"
                    :value="membershipPaymentPrice()"
                  />
                </v-flex>
                <v-flex xs12 md6 pr-2>
                  <v-text-field
                    readonly
                    label="Activated On"
                    :value="formatDate(memberItem.membership.current.subscriptionStarts, 'words')"
                  />
                </v-flex>
                <v-flex xs12 md6 pr-2>
                  <v-text-field
                    readonly
                    :error-messages="renewalError()"
                    label="Valid Until"
                    :value="formatDate(memberItem.membership.current.subscriptionEnds, 'words')"
                  />
                </v-flex>
                <v-flex xs12 pb-2>
                  <h3 class="mb-0">Volunteer Schedule</h3>
                </v-flex>
                <v-flex xs12 pa-0 pb-1>
                  <v-card pa-0 flat>
                    <v-card-title class="pa-0 cardTitle grey lighten-3">
                      <v-layout row wrap pa-1 pb-2>
                        <v-flex xs6 pt-1 pl-1 pa-0>
                          Rostered Days
                        </v-flex>
                        <v-flex xs4 pt-1 pa-0>
                          Role
                        </v-flex>
                        <v-flex xs2 pt-1 pa-0>
                          Attendance
                        </v-flex>
                      </v-layout>
                    </v-card-title>
                  </v-card>
                </v-flex>
                <v-flex xs12 v-for="(day, ix) in memberItem.membership.current.trackDuty" :key="ix" pb-1 pl-1>
                  <v-layout v-if="typeof day === 'string'" row wrap pa-0>
                    <v-flex xs6 pa-0>
                      <h2 class="subheading" :title="day">{{ formatDate(day, 'words') }}</h2>
                    </v-flex>
                    <v-flex xs4 pa-0>
                      <h2 class="subheading">Volunteer</h2>
                    </v-flex>
                    <v-flex xs2 pa-0>
                      --
                    </v-flex>
                  </v-layout>
                  <v-layout v-else row wrap pa-0>
                    <v-flex xs6 pa-0>
                      <h2 class="subheading" :title="day.date">{{ formatDate(day.date, 'words') }}</h2>
                    </v-flex>
                    <v-flex xs4 pa-0>
                      {{(day.isSupervisor ? 'Day Supervisor' : 'Volunteer')}}
                    </v-flex>
                    <v-flex v-if="day.attendance !== undefined" xs2 pa-0>
                      <span v-if="day.attendance" class="green--text">Attended</span>
                      <span v-else class="red--text">Absent</span>
                    </v-flex>
                    <v-flex v-else xs2 pa-0>
                      <span>--</span>
                    </v-flex>
                  </v-layout>
                  <v-divider/>
                </v-flex>
              </v-layout>
              <v-layout mt-2 row wrap>
                <v-divider class="orange sectionDivider" />
                <v-flex xs12 mt-2>
                  <h3>Membership History</h3>
                </v-flex>
              </v-layout>
              <v-layout row wrap mt-2>
                <v-flex xs12>
                  <v-card pa-0 flat>
                    <v-card-title class="pa-0 cardTitle grey lighten-3">
                      <v-layout row wrap pa-1 pb-2>
                        <v-flex xs2 pt-1 pl-1>
                          Status
                        </v-flex>
                        <v-flex xs4 pt-1>
                          Description
                        </v-flex>
                        <v-flex xs2 pt-1>
                          Start Date
                        </v-flex>
                        <v-flex xs2 pt-1>
                          End Date
                        </v-flex>
                        <v-flex xs2 pt-1>
                          Paid
                        </v-flex>
                      </v-layout>
                    </v-card-title>
                  </v-card>
                </v-flex>
              </v-layout>
              <v-layout row wrap pa-2>
                <v-flex xs12 v-if="memberItem.membership.current" :key="memberItem.membership.current.orderId">
                  <v-layout row wrap>
                    <v-flex xs2>
                      current
                    </v-flex>
                    <v-flex xs4>
                      {{memberItem.membership.current.name}}
                    </v-flex>
                    <v-flex xs2>
                      {{formatDate(memberItem.membership.current.subscriptionStarts)}}
                    </v-flex>
                    <v-flex xs2>
                      {{formatDate(memberItem.membership.current.subscriptionEnds)}}
                    </v-flex>
                    <v-flex xs2>
                      {{membershipPaymentPrice()}}
                    </v-flex>
                  </v-layout>
                </v-flex>
                <v-flex xs12 v-for="item in history" :key="item.orderId">
                  <v-layout row wrap>
                    <v-flex xs2>
                      {{item.status}}
                    </v-flex>
                    <v-flex xs4>
                      {{item.name}}
                    </v-flex>
                    <v-flex xs2>
                      {{formatDate(item.subscriptionStarts)}}
                    </v-flex>
                    <v-flex xs2>
                      {{formatDate(item.closedDate)}}
                    </v-flex>
                    <v-flex xs2>
                      {{membershipPaymentPrice(item.price)}}
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
              <v-layout row wrap mt-2>
                <v-divider class="orange sectionDivider" />
                <v-flex xs12 mt-2>
                  <h3>
                    <span>Club Involvement</span>
                    <v-menu offset-y top>
                      <template v-slot:activator="{ on }">
                        <v-icon size="30" class="iconBtn ml-2" v-on="on">more_horiz</v-icon>
                      </template>
                      <v-list class="pa-2">
                        <v-list-tile v-if="!isJoined('committee')" @click="openPromoteModal('committee', true)" key="committee">
                          <v-list-tile-title>
                            <h3 class="subheading">Make Committee Member</h3>
                          </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile v-if="!isJoined('executive')" @click="openPromoteModal('executive', true)" key="executive">
                          <v-list-tile-title>
                            <h3 class="subheading">Make Executive Board Member</h3>
                          </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile v-if="!isJoined('administrator')" @click="openPromoteModal('administrator', true)" key="admin">
                          <v-list-tile-title>
                            <h3 class="subheading" title="This feature is currently disabled">Make Administrator</h3>
                          </v-list-tile-title>
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                  </h3>
                </v-flex>
              </v-layout>
              <v-layout row wrap mt-2>
                <v-flex xs12>
                  <v-card pa-0 flat>
                    <v-card-title class="pa-0 cardTitle grey lighten-3">
                      <v-layout row wrap pa-1 pb-2>
                        <v-flex xs2 pt-1 pl-1>
                          Groups
                        </v-flex>
                        <v-flex xs2 pt-1>
                          Is Member
                        </v-flex>
                        <v-flex xs2 pt-1>
                          Opt-In
                        </v-flex>
                        <v-flex xs2 pt-1>
                          Started
                        </v-flex>
                        <v-flex xs2 pt-1>
                          Ended
                        </v-flex>
                        <v-flex xs2 pt-1>
                          Options
                        </v-flex>
                      </v-layout>
                    </v-card-title>
                  </v-card>
                </v-flex>
              </v-layout>
              <v-layout pa-0 pl-1 row wrap v-if="memberItem.committee">
                <v-flex xs2 pt-1 pl-1>
                  <strong>Committee</strong>
                </v-flex>
                <v-flex xs2 pt-1>
                  {{isJoined('committee') ? 'Yes' : 'No'}}
                </v-flex>
                <v-flex xs2 pt-1>
                  {{optinStatus('committee')}}
                </v-flex>
                <v-flex xs2 pt-1>
                  {{ memberItem.committee.since ? new Date(memberItem.committee.since).toDateString() : '-' }}
                </v-flex>
                <v-flex xs2 pt-1>
                  {{ !isJoined('committee') && memberItem.committee.ended ? new Date(memberItem.committee.ended).toDateString() : '-' }}
                </v-flex>
                <v-flex xs2 pt-1>
                  <v-menu offset-y top>
                    <template v-slot:activator="{ on }">
                      <v-icon size="30" class="iconBtn"  v-on="on">more_horiz</v-icon>
                    </template>
                    <v-list class="pa-2">
                      <v-list-tile v-if="!isJoined('committee')" @click="openPromoteModal('committee', true)" key="committee">
                        <v-list-tile-title>
                          <h3 class="subheading">Add to Committee</h3>
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile v-else @click="openPromoteModal('committee', false)" key="committee">
                        <v-list-tile-title>
                          <h3 class="subheading">Remove from Committee</h3>
                        </v-list-tile-title>
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </v-flex>
              </v-layout>
              <v-layout pa-0 pl-1 row wrap v-if="memberItem.executive">
                <v-flex xs2 pt-1 pl-1>
                  <strong>Executive</strong>
                </v-flex>
                <v-flex xs2 pt-1>
                  {{isJoined('executive') ? 'Yes' : 'No'}}
                </v-flex>
                <v-flex xs2 pt-1>
                  --
                </v-flex>
                <v-flex xs2 pt-1>
                  {{ memberItem.executive.since ? new Date(memberItem.executive.since).toDateString() : '-' }}
                </v-flex>
                <v-flex xs2 pt-1>
                  {{ !isJoined('executive') && memberItem.executive.ended ? new Date(memberItem.executive.ended).toDateString() : '-' }}
                </v-flex>
                <v-flex xs2 pt-1>
                  <v-menu offset-y top>
                    <template v-slot:activator="{ on }">
                      <v-icon size="30" class="iconBtn" v-on="on">more_horiz</v-icon>
                    </template>
                    <v-list class="pa-2">
                      <v-list-tile v-if="!isJoined('executive')" @click="openPromoteModal('executive', true)" key="executive">
                        <v-list-tile-title>
                          <h3 class="subheading">Add to Executive Board</h3>
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile v-else @click="openPromoteModal('executive', false)" key="executive">
                        <v-list-tile-title>
                          <h3 class="subheading">Remove from Executive Board</h3>
                        </v-list-tile-title>
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </v-flex>
              </v-layout>
              <v-layout pa-0 pl-1 row wrap v-if="memberItem.acl && memberItem.acl.admin">
                <v-flex xs2 pt-1 pl-1>
                  <strong>Administrator</strong>
                </v-flex>
                <v-flex xs2 pt-1>
                  {{isJoined('administrator') ? 'Yes' : 'No'}}
                </v-flex>
                <v-flex xs2 pt-1>
                  --
                </v-flex>
                <v-flex xs2 pt-1>
                  {{ memberItem.acl.admin.since ? memberItem.acl.admin.since : '-' }}
                </v-flex>
                <v-flex xs2 pt-1>
                  {{ !isJoined('administrator') && memberItem.acl.admin.ended ? new Date(memberItem.acl.admin.ended).toDateString() : '-' }}
                </v-flex>
                <v-flex xs2 pt-1>
                  <v-menu offset-y top>
                    <template v-slot:activator="{ on }">
                      <v-icon size="30" class="iconBtn" v-on="on">more_horiz</v-icon>
                    </template>
                    <v-list class="pa-2">
                      <v-list-tile v-if="!isJoined('administrator')" @click="openPromoteModal('administrator', true)" key="administrator">
                        <v-list-tile-title>
                          <h3 class="subheading">Add as Administrator</h3>
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile v-else @click="openPromoteModal('administrator', false)" key="administrator">
                        <v-list-tile-title>
                          <h3 class="subheading">Remove as Administrator</h3>
                        </v-list-tile-title>
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-flex>
        </v-layout>
      </v-flex>
      <v-dialog v-model="lifeConfirm" width="30%" lazy :fullscreen="isMobile">
        <v-card v-if="memberItem.membership.type === 'life'">
          <v-card-title class="pb-0">
            <v-layout row wrap pa-2>
              <v-flex xs12 class="text-xs-center">
                <h2 class="headline">Award Life Membership?</h2>
              </v-flex>
            </v-layout>
          </v-card-title>
          <v-card-actions class="pa-0">
            <v-layout row wrap justify-center text-xs-center v-show="lifeButtons">
              <v-flex xs12 pa-2>
                <p><em>This person already has a life membership!</em></p>
              </v-flex>
              <v-flex xs12 pa-2>
                <v-btn
                  depressed
                  block
                  color="grey"
                  class="white--text"
                  @click="lifeConfirm = false"
                >Close</v-btn>
              </v-flex>
            </v-layout>
          </v-card-actions>
        </v-card>
        <v-card v-else>
          <v-card-title class="pb-0">
            <v-layout row wrap pa-2>
              <v-flex xs12 class="text-xs-center">
                <h2 class="headline">Award Life Membership?</h2>
              </v-flex>
            </v-layout>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-layout row wrap text-xs-center pa-0>
              <v-flex xs12 pa-3>
                <h3 class="subheading"><strong>Member: </strong><span>{{`${memberItem.details.firstName} ${memberItem.details.lastName}`}}</span></h3>
                <h3 class="subheading"><strong>Member Since: </strong><span>{{formatDate(memberItem.membership.memberSince, 'since')}}</span></h3>
                <p class="pt-2">Are you sure you would like to grant <strong>{{memberItem.details.firstName}}</strong> with Life Membership status?</p>
              </v-flex>
              <v-flex xs12 pa-0>
                <v-alert :type="lifeAlert.type" :value="lifeAlert.status">
                    {{lifeAlert.message}}
                </v-alert>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions class="pa-0">
            <v-layout row wrap justify-center v-show="lifeButtons">
              <v-flex xs6 pa-2>
                <v-btn
                  depressed
                  block
                  color="grey"
                  class="white--text"
                  @click="lifeConfirm = false"
                >Cancel</v-btn>
              </v-flex>
              <v-flex xs6 pa-2>
                <v-btn
                  depressed
                  block
                  :loading="isUpgradeLoading"
                  color="info"
                  class="white--text"
                  @click="upgradeMembership()"
                >Grant Life Membership</v-btn>
              </v-flex>
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="deactivateConfirm" width="30%" persistent lazy :fullscreen="isMobile">
        <v-card>
          <v-card-title class="pb-0 error white--text">
            <v-layout row wrap pa-2>
              <v-flex xs12 class="text-xs-center pb-3">
                <h2 class="headline">Deactivate Current Membership</h2>
              </v-flex>
            </v-layout>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-layout row wrap pa-0 v-if="hasUserRegistered">
              <v-flex xs12 pa-3>
                <h3 class="subheading"><strong>Member: </strong><span>{{`${memberItem.details.firstName} ${memberItem.details.lastName}`}}</span></h3>
                <h3 class="subheading"><strong>Current Membership Started: </strong><span>{{formatDate(memberItem.membership.current.subscriptionStarts)}}</span></h3>
                <h3 class="subheading"><strong>Current Membership Ends: </strong><span>{{formatDate(memberItem.membership.current.subscriptionEnds)}}</span></h3>
                <p class="mt-2">
                  Are you sure you wish to cancel this member's current annual registration?
                </p>
              </v-flex>
              <v-flex xs12 pa-0>
                <v-alert type="error" :value=deactivateError >
                    There was a problem trying to deactivate this member!<br/>
                    <em>Description: "{{deactivateErrorMsg}}"</em>
                </v-alert>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions class="pa-0">
            <v-layout row wrap justify-center v-show="lifeButtons">
              <v-flex xs6 pa-2>
                <v-btn
                  depressed
                  block
                  color="grey"
                  class="white--text"
                  @click="deactivateConfirm = false; deactivateLoading = false; deactivateError = false"
                >Cancel</v-btn>
              </v-flex>
              <v-flex xs6 pa-2>
                <v-btn
                  depressed
                  block
                  :loading="deactivateLoading"
                  color="info"
                  class="white--text"
                  @click="deactivateMembership()"
                >Deactivate</v-btn>
              </v-flex>
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="promoteModal" width="30%" persistent lazy :fullscreen="isMobile">
        <v-card>
          <v-card-title class="pb-0">
            <v-layout row wrap pa-2>
              <v-flex xs12 class="text-xs-center">
                <h2 class="headline">{{promoteType ? 'Promote to Group' : 'Remove from Group'}}</h2>
              </v-flex>
            </v-layout>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-layout row wrap pa-0>
              <v-flex xs12 pa-3>
                <h3 class="subheading"><strong>Member to Promote: </strong><span>{{`${memberItem.details.firstName} ${memberItem.details.lastName}`}}</span></h3>
                <h3 class="subheading"><strong>Group: </strong><span>{{promoteTitle}}</span></h3>
                <p class="pt-2">Are you sure you want to <strong>{{promoteType ? 'add' : 'remove'}}</strong> this Member {{promoteType ? 'to' : 'from'}} this group?</p>
              </v-flex>
              <v-flex xs12 pa-0>
                <v-alert type="error" :value=promoteError >
                    There was an Error trying to update this member!<br/>
                    <em>Code: "{{promoteErrorMsg}}"</em>
                </v-alert>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions class="pa-0">
            <v-layout row wrap justify-center>
              <v-flex xs6 pa-2>
                <v-btn
                  depressed
                  block
                  color="grey"
                  class="white--text"
                  @click="promoteModal = false; promoteLoading = false; promoteError = false"
                >Cancel</v-btn>
              </v-flex>
              <v-flex xs6 pa-2>
                <v-btn
                  depressed
                  block
                  :loading="promoteLoading"
                  color="info"
                  class="white--text"
                  @click="updateMemberGroup()"
                >Confirm</v-btn>
              </v-flex>
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="resetModal" width="30%" persistent lazy :fullscreen="isMobile">
        <v-card>
          <v-card-title class="pb-0">
            <v-layout row wrap pa-2>
              <v-flex xs12 class="text-xs-center">
                <h2 class="headline">Reset Account Password</h2>
              </v-flex>
            </v-layout>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-layout row wrap pa-0>
              <v-flex xs12 pa-3>
                <h3 class="subheading"><strong>Member to Reset: </strong><span>{{`${memberItem.details.firstName} ${memberItem.details.lastName}`}}</span></h3>
                <p class="pt-2">Are you sure you want to reset this users password?</p>
              </v-flex>
              <v-flex xs12 pa-0>
                <v-alert type="error" :value=resetError >
                    There was an Error trying to update this member!<br/>
                    <em>Code: "{{resetErrorMsg}}"</em>
                </v-alert>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions class="pa-0">
            <v-layout row wrap justify-center>
              <v-flex xs6 pa-2>
                <v-btn
                  depressed
                  block
                  color="grey"
                  class="white--text"
                  @click="resetModal = false; resetLoading = false; resetError = false"
                >Cancel</v-btn>
              </v-flex>
              <v-flex xs6 pa-2>
                <v-btn
                  depressed
                  block
                  :loading="resetLoading"
                  color="info"
                  class="white--text"
                  @click="resetUserPwd()"
                >Confirm</v-btn>
              </v-flex>
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="archiveConfirm" width="30%" persistent lazy :fullscreen="isMobile">
        <v-card>
          <v-card-title class="pb-0 error white--text">
            <v-layout row wrap pa-2>
              <v-flex xs12 class="text-xs-center pb-3">
                <h2 class="headline">Archive Member</h2>
              </v-flex>
            </v-layout>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-layout row wrap pa-0>
              <v-flex xs12 pa-3>
                <h3 class="subheading"><strong>Member: </strong><span>{{`${memberItem.details.firstName} ${memberItem.details.lastName}`}}</span></h3>
                <h3 class="subheading"><strong>Email: </strong><span>{{memberItem.details.email}}</span></h3>
                <h3 class="subheading"><strong>Status: </strong><span :class="memberStatusClass()">{{memberStatus()}}</span></h3>
                <p class="mt-2">
                  Are you sure you wish to archive this member?
                </p>
              </v-flex>
              <v-flex xs12 pa-0>
                <v-alert type="warning" :value="!canArchiveMember">
                  This member has an active membership and cannot be archived. Please deactivate their membership first.
                </v-alert>
              </v-flex>
              <v-flex xs12 pa-0>
                <v-alert type="error" :value="archiveError">
                  There was a problem trying to archive this member!<br/>
                  <em>Description: "{{archiveErrorMsg}}"</em>
                </v-alert>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions class="pa-0">
            <v-layout row wrap justify-center>
              <v-flex xs6 pa-2>
                <v-btn
                  depressed
                  block
                  color="grey"
                  class="white--text"
                  @click="archiveConfirm = false; archiveLoading = false; archiveError = false"
                >Cancel</v-btn>
              </v-flex>
              <v-flex xs6 pa-2>
                <v-btn
                  depressed
                  block
                  :loading="archiveLoading"
                  :disabled="!canArchiveMember"
                  color="error"
                  class="white--text"
                  @click="archiveMember()"
                >Archive</v-btn>
              </v-flex>
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>
  </v-container>
</template>

<script>
// import store from '@/store'
// import TestMemeberList from '@/data/TestAccountMembers'
import { mapGetters, mapActions } from 'vuex'

export default {
  computed: {
    ...mapGetters({
      memberItem: 'getMemberItem',
      loadingError: 'getMemberItemError'
    }),
    isMemberItemLoaded () {
      if (this.memberItem === null) return false
      return true
    },
    hasUserRegistered () {
      try {
        if (this.memberItem.membership.current) {
          return true
        }
        return false
      } catch (e) {
        return false
      }
    },
    hasMetaChanged () {
      console.log('is changed?')
      if (this.email !== '' && this.email !== this.memberItem.details.email) return true
      if (this.firstName !== '' && this.firstName !== this.memberItem.details.firstName) return true
      if (this.lastName !== '' && this.lastName !== this.memberItem.details.lastName) return true
      if (this.phoneNumber !== '' && '+61' + this.phoneNumber !== this.memberItem.details.phoneNumber) return true
      if (this.memberItem.details.secondary) {
        if (this.secondName !== '' && this.secondName !== this.memberItem.details.secondary.name) return true
        if (this.secondNumber !== '' && this.secondNumber !== this.memberItem.details.secondary.number) return true
      }
      if (this.memberItem.details.address) {
        if (this.street !== '' && this.street !== this.memberItem.details.address.street) return true
        if (this.suburb !== '' && this.suburb !== this.memberItem.details.address.suburb) return true
        if (this.postcode !== '' && this.postcode !== this.memberItem.details.address.postcode) return true
        if (this.state !== '' && this.state !== this.memberItem.details.address.state) return true
      }

      if (this.ridersToggle) return true

      console.log('no change')
      return false
    },
    isMemberExecutive () {
      try {
        return this.memberItem.executive.active
      } catch (e) {
        return false
      }
    },
    isMemberCommittee () {
      try {
        return this.memberItem.committee.active
      } catch (e) {
        return false
      }
    },
    history () {
      let history = this.memberItem.membershipHistory || []
      return history.reverse()
    },
    isMobile () {
      return (window.innerWidth < 960)
    },
    isUpgradeLoading () {
      return this.lifeLoading
    },
    canArchiveMember () {
      return this.memberStatus() !== 'Active'
    }
  },
  methods: {
    ...mapActions([
      'adminLoadMember',
      'adminGrantLife',
      'adminDeactivateMembership',
      'promoteMember',
      'resetMemberPwd',
      'resendAccountActivation',
      'adminUpdateProfile',
      'adminGetCertificate',
      'archiveUsers'
    ]),
    membershipPaymentPrice (price = null) {
      let value = price
      if (price === null) value = this.memberItem.membership.current.price

      if (this.memberItem.membership.type !== 'life' && this.memberItem.membership.type !== 'sponsor') {
        if (value !== 0 && value !== '0') return `$${value}`
        return ''
      }

      return 'Free'
    },
    renewalError: function () {
      if (this.isMembershipExpired()) return ['Your annual membership needs to be renewed']
    },
    isMembershipClose: function () {
      try {
        let close = new Date(this.memberItem.membership.current.subscriptionEnds)
        close.setMonth(close.getMonth() - 1)
        return (new Date() >= close)
      } catch (e) {
        return false
      }
    },
    isMembershipExpired: function () {
      try {
        if (!this.memberItem.membership.current) return true
        const ends = new Date(new Date(this.memberItem.membership.current.subscriptionEnds).toDateString())
        const now = new Date(new Date().toDateString())
        return (ends < now)
      } catch (e) {
        return false
      }
    },
    memberStatus: function () {
      if (!this.memberItem || !this.memberItem.status || this.memberItem.status === 'unconfirmed') return 'Unconfirmed'
      if (this.memberItem.status === 'unregistered') return 'Inactive'
      if (this.isMembershipExpired()) return 'Renewal Due'
      return 'Active'
    },
    memberStatusClass: function (member) {
      if (!this.memberItem || !this.memberItem.status) return 'unconfirmed'
      if (this.memberItem.status === 'unregistered') return 'unregistered'
      if (this.isMembershipExpired()) return 'renew'
      return 'active'
    },
    userStatusClass: function (member) {
      if (!this.userStatus) return 'error--text'
      else if (this.userStatus === 'CONFIRMED') return 'success--text'
      else if (this.userStatus === 'FORCE_CHANGE_PASSWORD') return 'warning--text'
      return 'error--text'
    },
    formatDate: function (timestring, format) {
      if (!timestring) return ''

      let time = new Date(timestring)
      switch (format) {
        case 'full':
          return `${time.toDateString()}, ${time.toLocaleTimeString()}`
        case 'words':
          return `${time.toDateString()}`
        case 'date':
          return `${time.getDate()}/${time.getMonth() + 1}/${time.getFullYear()}`
        case 'since':
          let newFormat = `${time.getDate()}/${time.getMonth() + 1}/${time.getFullYear()}`
          let nowYear = new Date().getFullYear()
          let yearDiff = nowYear - time.getFullYear()
          if (yearDiff === 0) return newFormat
          if (yearDiff === 1) return newFormat + ' (1 Year)'
          return newFormat + ` (${yearDiff} Years)`
        default:
          return `${time.getDate()}/${time.getMonth() + 1}/${time.getFullYear()}`
      }
    },
    isRenewalDue: function () {
      let now = new Date()
      let soon = new Date(this.subscription.current.subscriptionEnds)
      soon.setMonth(soon.getMonth() - 1)
      console.log(`now: ${now}`)
      console.log(`1 month to renewal: ${soon}`)
      if (now.getTime() >= soon.getTime()) {
        return true
      }

      return false
    },
    async deactivateMembership () {
      try {
        if (this.deactivateLoading) return false
        this.deactivateLoading = true
        this.deactivateError = false

        let response = await this.adminDeactivateMembership({ userid: this.memberItem.id })
        this.deactivateLoading = false
        if (response.error !== undefined) {
          this.deactivateError = true
          this.deactivateErrorMsg = response.error.message
          return false
        }

        this.deactivateConfirm = false
        this.deactivateError = false
        this.deactivateErrorMsg = ''

        this.loadData()
        return true
      } catch (e) {
        this.deactivateLoading = false
        this.deactivateConfirm = true
        this.deactivateErrorMsg = e.message
        return false
      }
    },
    async archiveMember () {
      try {
        if (this.archiveLoading) return false
        this.archiveLoading = true
        this.archiveError = false

        console.log('Archiving member:', this.memberItem.id)
        const response = await this.archiveUsers({ userIds: [this.memberItem.id] })

        this.archiveLoading = false
        if (response.error) {
          this.archiveError = true
          this.archiveErrorMsg = response.error.message || 'An error occurred while archiving the member'
          return false
        }

        console.log('Member archived successfully')
        this.archiveConfirm = false
        this.archiveError = false
        this.archiveErrorMsg = ''

        this.$router.push('/admin/members')
        return true
      } catch (e) {
        console.log('Error archiving member:', e)
        this.archiveLoading = false
        this.archiveError = true
        this.archiveErrorMsg = e.message
        return false
      }
    },
    async upgradeMembership () {
      this.lifeLoading = true
      this.lifeAlert.type = 'error'
      this.lifeAlert.status = false
      this.lifeAlert.message = ''

      const id = this.memberItem.id
      let response = await this.adminGrantLife({ id })
      this.lifeLoading = false
      if (response.error) {
        this.lifeAlert.type = 'error'
        this.lifeAlert.status = true
        this.lifeAlert.message = 'There was an error upgrading this account. Please wait a few moments and try again'
        return false
      }

      this.lifeAlert.type = 'success'
      this.lifeAlert.status = true
      this.lifeAlert.message = 'Life Membership has been Granted!'
      this.lifeButtons = false
      setTimeout(() => {
        this.lifeAlert.status = false
        this.lifeConfirm = false
        this.lifeButtons = true
        this.adminLoadMember(this.$route.params.username)
      }, 1000)
      return false
    },
    async loadData () {
      await this.adminLoadMember(this.$route.params.username)
      if (!this.memberItem || !this.memberItem.details) return false
      this.firstName = this.memberItem.details.firstName || null
      this.lastName = this.memberItem.details.lastName || null
      this.email = this.memberItem.details.email || null
      this.phoneNumber = this.memberItem.details.phoneNumber || null
      this.phoneNumber = this.phoneNumber.replace('+61', '')
      this.mobileNumber = this.memberItem.details.mobileNumber || null
      if (this.memberItem.details.secondary) {
        this.secondName = this.memberItem.details.secondary.name || null
        this.secondNumber = this.memberItem.details.secondary.number || null
      }
      if (this.memberItem.details.address) {
        this.street = this.memberItem.details.address.street || null
        this.suburb = this.memberItem.details.address.suburb || null
        this.postcode = this.memberItem.details.address.postcode || null
        this.state = this.memberItem.details.address.state || null
      }

      if (this.memberItem.accountMembers) {
        this.riders = this.memberItem.accountMembers || []
      }

      this.userStatus = this.memberItem.UserStatus || null
    },
    optinStatus (group = null) {
      try {
        if (!group) return false
        if (group === 'committee') {
          return (this.memberItem.committee.optin === true) ? 'Yes' : 'No'
        } else if (group === 'executive') {
          return (this.memberItem.executive.optin === true) ? 'Yes' : 'No'
        }
        return 'No'
      } catch (e) {
        return 'No'
      }
    },
    isJoined (group = null) {
      try {
        if (!group) return false
        switch (group) {
          case 'committee':
            return (this.memberItem.committee.active === true)
          case 'executive':
            return (this.memberItem.executive.active === true)
          case 'administrator':
            return (this.memberItem.acl.admin.active === true)
          default:
            return false
        }
      } catch (e) {
        return false
      }
    },
    openPromoteModal (group, type) {
      console.log('Open Promote Modal: ', group, type)
      if (!group || this.groups.indexOf(group) < 0) return false
      this.promoteTitle = group
      this.promoteType = type
      this.promoteModal = true
    },
    async updateMemberGroup () {
      try {
        if (this.promoteLoading) return false
        this.promoteLoading = true
        this.promoteError = false
        const args = {
          userid: this.memberItem.id,
          newTitle: this.promoteTitle,
          isAdding: this.promoteType
        }

        console.log('Update Group Args: ', args)

        let response = await this.promoteMember(args)
        this.promoteLoading = false
        if (response.error !== undefined) {
          this.promoteError = true
          this.promoteErrorMsg = response.error.message
          return false
        }

        this.promoteError = false
        this.promoteErrorMsg = false
        this.promoteModal = false
        this.loadData()
        return true
      } catch (e) {
        this.promoteLoading = false
        this.promoteUser = true
        this.promoteErrorMsg = e.message
        return false
      }
    },
    async resetUserPwd () {
      try {
        if (this.resetLoading) return false
        this.resetLoading = true
        this.resetError = false
        const args = {
          userid: this.memberItem.id
        }

        console.log('Reset User Pwd: ', args)

        let response = await this.resetMemberPwd(args)
        this.resetLoading = false
        if (response.error !== undefined) {
          this.resetError = true
          this.resetErrorMsg = response.error.message
          return false
        }

        this.resetError = false
        this.resetErrorMsg = false
        this.resetModal = false
        this.loadData()
        return true
      } catch (e) {
        this.resetLoading = false
        this.resetUser = true
        this.resetErrorMsg = e.message
        return false
      }
    },
    async resendActivationEmail () {
      try {
        console.log('Resending Account Acitvation Email...')
        if (this.resendLoading) return false
        this.resendLoading = true
        const args = {
          userid: this.memberItem.id
        }

        console.log('Resend Args: ', args)

        let response = await this.resendAccountActivation(args)
        console.log(response)
        this.resendLoading = false
        if (response.error !== undefined) {
          console.log(response.error.message)
          return false
        }

        this.loadData()
        return true
      } catch (e) {
        this.resendLoading = false
        console.log(e.message)
        return false
      }
    },
    cancelChanges () {
      this.metaSaving = false
      this.metaError = false
      this.firstName = this.memberItem.details.firstName || null
      this.lastName = this.memberItem.details.lastName || null
      this.email = this.memberItem.details.email || null
      this.phoneNumber = this.memberItem.details.phoneNumber || null
      this.phoneNumber = this.phoneNumber.replace('+61', '')
      if (this.memberItem.details.secondary) {
        this.secondName = this.memberItem.details.secondary.name || null
        this.secondNumber = this.memberItem.details.secondary.number || null
      } else {
        this.secondName = ''
        this.secondNumber = ''
      }
      if (this.memberItem.details.address) {
        this.street = this.memberItem.details.address.street || null
        this.suburb = this.memberItem.details.address.suburb || null
        this.postcode = this.memberItem.details.address.postcode || null
        this.state = this.memberItem.details.address.state || null
      }

      if (this.memberItem.accountMembers) {
        this.riders = this.memberItem.accountMembers || []
      }
    },
    async saveChanges () {
      if (this.metaSaving) return false
      this.metaSaving = true
      this.metaSaved = false
      this.metaError = false

      let number = null
      if (this.phoneNumber) {
        number = this.phoneNumber.replace('+61', '')
        number = '+61' + number
      }
      let changes = {
        username: this.memberItem.id,
        details: {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email.toLowerCase(),
          phoneNumber: number,
          address: {
            street: this.street,
            suburb: this.suburb,
            postcode: this.postcode,
            state: this.state
          },
          secondary: {
            name: this.secondName,
            number: this.secondNumber
          }
        },
        accountMembers: this.riders
      }

      try {
        await this.adminUpdateProfile(changes)
        this.metaSaving = false
        this.metaSaved = true
        setTimeout(() => {
          this.metaSaved = false
        }, 1000)
      } catch (e) {
        this.metaSaving = false
        this.metaError = true
        setTimeout(() => {
          this.metaError = false
        }, 1000)
      }
    },
    async downloadCertificate () {
      try {
        if (this.pdfLoading) return false
        this.pdfLoading = true
        console.log('Retrieving User Certificate:', this.memberItem.id)
        let result = await this.adminGetCertificate({ username: this.memberItem.id })
        this.pdfLoading = false
        if (!result.error && result.pdfLink) {
          console.log('My Cert Link:', result.pdfLink)
          let win = window.open(result.pdfLink, '_blank')
          win.focus()
          return true
        }

        alert('An Error occurred generating your certificate. Please wait a few moments and try again')
        return false
      } catch (e) {
        this.pdfLoading = false
        alert('An Error occurred generating your certificate. Please wait a few moments and try again')
        return false
      }
    }
  },
  data () {
    return {
      pdfLoading: false,
      metaSaving: false,
      metaSaved: false,
      metaError: false,
      memberMenu: null,
      archiveConfirm: false,
      archiveLoading: false,
      archiveError: false,
      archiveErrorMsg: '',
      deactivateConfirm: false,
      deactivateLoading: false,
      deactivateError: false,
      deactivateErrorMsg: '',
      lifeLoading: false,
      lifeConfirm: false,
      lifeButtons: true,
      lifeAlert: {
        type: 'error',
        status: false,
        message: ''
      },
      promoteModal: false,
      promoteLoading: false,
      promoteTitle: null,
      promoteType: false,
      promoteError: false,
      promoteErrorMsg: '',
      resetModal: false,
      resetLoading: false,
      resetTitle: null,
      resetType: false,
      resetError: false,
      resetErrorMsg: '',
      resendLoading: false,
      userStatus: null,
      userLastModifiedDate: null,
      userCreateDate: null,
      email: null,
      firstName: null,
      lastName: null,
      phoneNumber: null,
      mobileNumber: null,
      secondName: null,
      secondNumber: null,
      ridersToggle: false,
      street: null,
      suburb: null,
      postcode: null,
      state: null,
      riders: [],
      stateItems: ['NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
      groups: ['administrator', 'executive', 'committee']
    }
  },
  mounted () {
    this.loadData()
  }
}
</script>
<style>
  #specialOffer {
    font-size: 2rem;
    background-color: darkgreen;
    padding: 5px 20px;
    border-radius: 3px;
    width: fit-content;
    color: white;
  }
  .moreBtn {
    height: 24px!important;
    width: 24px!important;
    position: relative;
    top: -4px;
  }
  .iconBtn {
    border-radius: 15px;
    height: 20px;
    -webkit-transition: background-color 0.1s;
    transition: background-color 0.1s;
  }
  .memberStatus.unconfirmed {
    color: red;
  }
  .memberStatus.unregistered {
    color: orange;
  }
  .memberStatus.renew {
    color: orange;
  }
  .memberStatus.active {
    color: green;
  }
  .iconBtn:hover {
    background-color: lightgrey;
  }
</style>
